import supabase from 'modules/core/supabase/supabase';
import { type IBroadcast, isNoRowsError } from 'modules/core/supabase/tables/broadcast';
import { setBroadcastState, setIsBroadcastInitialized } from 'modules/overlay-v2/store/overlay/overlay';
import { readinessTaskFailed, readinessTaskStarted, readinessTaskSucceeded } from 'modules/overlay-v2/store/readiness/readiness';
import { selectRetryToken } from 'modules/overlay-v2/store/readiness/selectors';
import { toErrorMessage } from 'modules/overlay-v2/domain/utils/to-error-message';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';

function useInitBroadcast() {
  const dispatch = useAppDispatch();
  const { deviceId } = useParams();
  const id = useMemo(() => (deviceId ? Number(deviceId) : undefined), [deviceId]);

  const [broadcast, setBroadcast] = useState<IBroadcast | undefined>(supabase.broadcast.data.value);

  // `retryToken` в зависимостях обязателен: без него сорвавшийся запрос трансляции
  // навсегда превращался в «Матч в приложении ещё не запущен» — оператору советовали
  // запустить уже идущий матч, а повтор до устройства не доходил.
  const retryToken = useAppSelector(selectRetryToken);

  useEffect(() => {
    if (!id) return;
    dispatch(setIsBroadcastInitialized(false));
    dispatch(readinessTaskStarted('device'));
    supabase.broadcast
      .fetchByDeviceId(id)
      .then((result) => {
        supabase.broadcast.data.value = result;
        dispatch(readinessTaskSucceeded('device'));
      })
      .catch((e) => {
        console.error(e);
        // `.single()` считает ошибкой и пустую выборку. «Трансляции нет» — это не сбой
        // связи, а обычное состояние: матч ещё не запустили. Красным помечаем только
        // настоящие отказы, иначе оператору советуют чинить то, что не сломано.
        if (isNoRowsError(e)) {
          supabase.broadcast.data.value = undefined;
          dispatch(readinessTaskSucceeded('device'));
          return;
        }
        // Прежнюю трансляцию не стираем: сорванный запрос ничего не говорит о том, идёт
        // ли матч. Раньше очистка стояла безусловно, и повтор посреди эфира обнулял
        // таймер, период и очередь событий.
        dispatch(readinessTaskFailed({ id: 'device', error: toErrorMessage(e) }));
      })
      .finally(() => {
        dispatch(setIsBroadcastInitialized(true));
      });

    supabase.broadcast.observeByDeviceId(id);

    return () => {
      supabase.broadcast.stopObserve();
    };
  }, [id, dispatch, retryToken]);

  useEffect(() => {
    const callback = (newValue: IBroadcast | undefined) => {
      setBroadcast(newValue);
      dispatch(setBroadcastState(newValue));
      console.debug('broadcast', newValue);
    };

    supabase.broadcast.data.subscribe(callback);

    return () => {
      supabase.broadcast.data.unsubscribe(callback);
    };
  }, [dispatch]);

  return broadcast;
}

export default useInitBroadcast;
